import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import { fetchEventLive } from "core-integration/src/store/elements/thunks";
import {
  getEntry,
  getEntryEventFormation,
  getEntryEventPicks,
} from "core-integration/src/store/entries/reducers";
import {
  fetchEntryEventPicks,
  fetchEntrySummary,
} from "core-integration/src/store/entries/thunks";
import { IPickLight } from "core-integration/src/store/entries/types";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import {
  getRules,
  getSettings,
} from "core-integration/src/store/game/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import type { EntryCrestData } from "core-integration/src/store/ui";
import range from "lodash/range";
import { PagerButton } from "plos/src/components/buttons/PagerButton";
import { Fixtures } from "plos/src/components/Fixtures";
import Link from "plos/src/components/links/Link";
import PageTitle from "plos/src/components/PageTitle";
import Pitch from "plos/src/components/Pitch";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { ElementIconLink } from "plos/src/components/tooltips/ElementIconLink";
import { entryEventMain, leftSidebarLayout } from "plos/src/layouts";
import { edgeToEdge } from "plos/src/styles";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useAdobeContext } from "../../contexts/AdobeContext";
import { OverlayProvider } from "../../contexts/OverlayContext";
import { useTrackingContext } from "../../contexts/TrackingContext";
import { usePitchView } from "../../hooks/usePitchView";
import DreamTeam from "../../img/icons/dreamteam.svg?react";
import { Loading } from "../App";
import { EntryCrest } from "../crests/EntryCrest";
import EntryInfo from "../EntryInfo";
import { HelmetHead } from "../HelmetHead";
import { ArrowRight } from "../icons/Arrows";
import { LeaderboardAd } from "../LeaderboardAd";
import { PitchControls } from "../PitchControls";
import { NotFound } from "../Routes";
import { dreamTeamIcon } from "../TeamPitchElement/teamPitchElement.css";
import { ContextualHelpSheet } from "../Tutorials";
import PointsHelp from "../Tutorials/HelpPages/PointsHelp";
import { AdobeTemplateModal } from "./AdobeTemplateModal";
import { Autosubs } from "./Autosubs";
import { Bench, BenchUnit } from "./Bench";
import ElementExplainSheet from "./ElementExplainSheet";
import EntryEventTable from "./EntryEventTable";
import {
  boldLink,
  dreamTeamWrap,
  entryCrestStyles,
  eventHeaderWrapper,
  eventHeading,
  eventPager,
  nextEvent,
  previousEvent,
  teamPitchWrap,
  teamTitleWrapper,
} from "./myTeam.css";
import PitchFormation from "./PitchFormation";
import PointsScoreboard from "./PointsScoreboard";

const EntryEvent = () => {
  const { t } = useTranslation("points");

  const { firePageViewEvent } = useTrackingContext();
  const { activeView, handleViewChange } = usePitchView();
  const { getTemplate } = useAdobeContext();
  const [pickForMenu, setPickForMenu] = useState<IPickLight | null>(null);

  const [showGWSummaryDialog, setShowGWSummaryDialog] = useState(false);
  const [adobeGameweekTemplateUrl, setAdobeGameweekTemplateUrl] = useState<
    string | undefined
  >();
  const [adobeSeasonTemplateUrl, setAdobeSeasonTemplateUrl] = useState<
    string | undefined
  >();
  const [hasSeenGWModal, setHasSeenGWModal] = useState(false);

  const dispatch = useDispatch<ThunkDispatch>();
  const params = useParams();

  const entryId = Number(params.entryId);
  const eventId = Number(params.eventId);

  const player = useSelector((state: RootState) => getPlayerData(state));

  const entry = useSelector((state: RootState) => getEntry(state, entryId));

  const event = useSelector(
    (state: RootState) => getEventsById(state)[eventId],
  );

  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, eventId),
  );

  const now = useSelector(getCurrentEvent);
  const nxt = useSelector(getNextEvent);
  const entryData = useSelector((state: RootState) =>
    getEntryEventPicks(state, entryId, eventId),
  );

  const formation = useSelector((state: RootState) =>
    getEntryEventFormation(state, entryId, eventId),
  );
  const mine = player?.entry === entryId;
  const rules = useSelector(getRules);
  const settings = useSelector(getSettings);

  const handleShowMenuForPickElement = (element: number) => {
    const matches = entryData?.picks.filter((p) => p.element === element);
    if (matches?.length) {
      setPickForMenu(matches[0]);
    }
  };

  const handleClosePlayerSheet = () => {
    setPickForMenu(null);
  };

  const renderDreamTeam = (pick: IPickLight) =>
    elementsDataById && elementsDataById[pick.element]?.stats?.in_dreamteam ? (
      <ElementIconLink
        to={`/team-of-the-week/${eventId}`}
        label={t("teamOfTheWeek", "Team of the Week")}
      >
        <DreamTeam className={dreamTeamIcon} aria-hidden />
      </ElementIconLink>
    ) : null;

  const fetchData = useCallback(() => {
    dispatch(fetchEntrySummary(entryId));
    dispatch(fetchEntryEventPicks(entryId, eventId));
    dispatch(fetchEventLive(eventId));
  }, [dispatch, entryId, eventId]);

  useEffect(() => {
    if (entryId) {
      fetchData();
    }
  }, [dispatch, fetchData, entryId]);

  useEffect(() => {
    firePageViewEvent(
      "fantasy points",
      activeView === "pitch" ? "pitch view" : "list view",
    );
  }, []);

  useEffect(() => {
    if (entry && event) {
      (async () => {
        // Avoid the modal popping up as users navigate to previous gameweeks or looking at other teams
        const isCurrentEvent = now && event.id === now.id;
        const canShowAdobeSummary = entry.id && isCurrentEvent && mine;

        if (!canShowAdobeSummary) {
          return;
        }

        if (settings?.static_content_url) {
          const gwStorageKey = `adobe-template-shown-gw-${event.id}-user-${entry.id}`;
          const [gameweekTemplateUrl, seasonTemplateUrl] = await Promise.all([
            getTemplate({
              baseUrl: settings.static_content_url,
              entry,
              event,
              type: "gameweek",
            }),
            getTemplate({
              baseUrl: settings.static_content_url,
              entry,
              event,
              type: "season",
            }),
          ]);

          // Standard gameweek, gameweek template ready
          if (gameweekTemplateUrl && !seasonTemplateUrl) {
            setAdobeGameweekTemplateUrl(gameweekTemplateUrl);

            // Check if user has already seen this modal for this gameweek
            const hasSeenModal = localStorage.getItem(gwStorageKey);

            if (!hasSeenModal) {
              setShowGWSummaryDialog(true);
              localStorage.setItem(gwStorageKey, "true");
            }
          }
          // End of season, season template ready (gameweek template should always be ready by this point but check anyway)
          else if (seasonTemplateUrl) {
            if (gameweekTemplateUrl) {
              setAdobeGameweekTemplateUrl(gameweekTemplateUrl);
            }
            setAdobeSeasonTemplateUrl(seasonTemplateUrl);

            // If the user already saw the GW recap, show only the season button
            setHasSeenGWModal(Boolean(localStorage.getItem(gwStorageKey)));

            // Check if user has already seen the season recap modal
            const seasonStorageKey = `adobe-template-shown-season-user-${entry.id}`;
            const hasSeenModal = localStorage.getItem(seasonStorageKey);

            if (!hasSeenModal) {
              setShowGWSummaryDialog(true);
              localStorage.setItem(seasonStorageKey, "true");
            }
          }
        }
      })();
    }
  }, [settings, entry, event, now, mine]);

  // Handle unknown and unstarted events
  if (!now || !event || eventId > now.id) {
    return <NotFound />;
  }

  if (!entry || !entryData) {
    return <Loading />;
  }

  const { name: entryName, started_event: startedEvent } = entry;

  const canNavigateToNextEvent = eventId < now.id;
  const canNavigateToMyTeam = mine && nxt && eventId === now.id;
  const canNavigateToPreviousEvent = eventId > startedEvent;

  const eventRank =
    eventId === now.id
      ? entry.summary_event_rank
      : entryData.entry_history.rank;

  const {
    active_chip: activeChip,
    picks,
    automatic_subs: automaticSubs,
  } = entryData;

  const { squad_squadplay: squadPlay, squad_squadsize: squadSize } = rules;
  const entryCrestData: EntryCrestData = {
    id: entry.id,
    name: entry.name,
    club_badge_src: entry.club_badge_src,
  };

  // Handle events before the entry started
  return (
    <OverlayProvider defaultOverlay="points" page="entry">
      <LeaderboardAd slot="Leaderboard_Points" id="ism-points-ad" />

      <HelmetHead
        title={t(
          "metadata.title",
          "View Latest Gameweek Points | Fantasy Premier League",
        )}
        description={t(
          "metadata.description",
          "To view the latest Fantasy Premier League Gameweek points, visit the official website of the Premier League.",
        )}
      />
      <ContextualHelpSheet
        title={t("contextualHelp.points.title", {
          ns: "help",
          defaultValue: "The Points page explained",
        })}
      >
        <PointsHelp />
      </ContextualHelpSheet>
      <div className={leftSidebarLayout}>
        <div className={entryEventMain}>
          <section>
            <SurfaceContainer>
              <div className={teamPitchWrap}>
                <div className={teamTitleWrapper}>
                  <div className={entryCrestStyles}>
                    <EntryCrest
                      entryCrestData={entryCrestData}
                      dimension={35}
                    />
                  </div>
                  <PageTitle title={entryName} />
                </div>
                <div className={eventHeaderWrapper}>
                  <div className={eventHeading}>
                    <h2>{event.name}</h2>
                  </div>
                  {(eventId > startedEvent || eventId < now.id) && (
                    <div className={eventPager}>
                      <PagerButton
                        url={
                          canNavigateToPreviousEvent
                            ? `/entry/${entryId}/event/${eventId - 1}`
                            : undefined
                        }
                        className={previousEvent}
                        direction="left"
                        isDisabled={!canNavigateToPreviousEvent}
                        label={t("previousGameweek", "Previous Gameweek")}
                      />
                      {canNavigateToNextEvent ? (
                        <PagerButton
                          url={`/entry/${entryId}/event/${eventId + 1}`}
                          className={nextEvent}
                          direction="right"
                          label={t("nextGameweek", "Next Gameweek")}
                        />
                      ) : canNavigateToMyTeam ? (
                        <PagerButton
                          url={`/my-team`}
                          className={nextEvent}
                          direction="right"
                          label={t("pickTeam", "Pick Team")}
                        />
                      ) : (
                        <>&nbsp;</>
                      )}
                    </div>
                  )}
                </div>
                <PointsScoreboard
                  entryData={entryData}
                  entryId={entryId}
                  eventId={eventId}
                  fetchData={fetchData}
                  event={event}
                  eventRank={eventRank}
                />

                <div className={dreamTeamWrap}>
                  <Link
                    to={`/team-of-the-week/${eventId}`}
                    className={boldLink}
                  >
                    <DreamTeam width={16} height={16} />
                    <span>{t("teamOfTheWeek", "Team of the Week")}</span>
                    <ArrowRight width={16} height={16} />
                  </Link>
                </div>

                <PitchControls
                  activeView={activeView}
                  handleViewChange={handleViewChange}
                />

                {activeView === "pitch" && (
                  <div className={edgeToEdge}>
                    <Pitch sponsor="default">
                      <PitchFormation
                        chipName={activeChip}
                        eventId={eventId}
                        formation={formation}
                        picks={picks}
                        renderDreamTeam={renderDreamTeam}
                        renderElementMenu={handleShowMenuForPickElement}
                      />
                      <Bench variant={activeChip}>
                        {range(squadPlay, squadSize).map((e, i) => (
                          <BenchUnit
                            key={e}
                            eventId={eventId}
                            index={i}
                            pick={picks[e]}
                            renderDreamTeam={renderDreamTeam}
                            renderElementMenu={() =>
                              handleShowMenuForPickElement(picks[e].element)
                            }
                          />
                        ))}
                      </Bench>
                    </Pitch>
                  </div>
                )}
                {activeView === "list" && (
                  <EntryEventTable
                    picks={picks}
                    renderElementMenu={handleShowMenuForPickElement}
                    chipName={activeChip}
                    dataById={elementsDataById}
                    elementsById={elementsById}
                    renderDreamTeam={renderDreamTeam}
                  />
                )}
                {pickForMenu && (
                  <ElementExplainSheet
                    elementId={pickForMenu.element}
                    eventId={eventId}
                    handleClose={handleClosePlayerSheet}
                    open={Boolean(pickForMenu.element)}
                  />
                )}
                <Autosubs
                  automaticSubs={automaticSubs}
                  elementsById={elementsById}
                  event={event}
                />
              </div>
            </SurfaceContainer>
          </section>

          <section>
            <SurfaceContainer>
              <Fixtures eventId={eventId} />
            </SurfaceContainer>
          </section>
        </div>

        <section>
          <SurfaceContainer>
            <EntryInfo entryId={entryId} />
          </SurfaceContainer>
        </section>

        <AdobeTemplateModal
          entry={entry}
          handleClose={() => setShowGWSummaryDialog(false)}
          isOpen={showGWSummaryDialog}
          adobeGameweekTemplateUrl={adobeGameweekTemplateUrl}
          adobeSeasonTemplateUrl={adobeSeasonTemplateUrl}
          eventId={eventId}
          hasSeenGWModal={hasSeenGWModal}
        />
      </div>
    </OverlayProvider>
  );
};

export default EntryEvent;
